import type { PromotionCountFilters } from '../hooks/queries/usePromotionCount';
import type { ConstraintRowData, FormData, Level } from '../components/OptimizePlan/types';
import type { FormFieldConfig } from '../../../config/eventsConfig';
import type { LimitConstraintFieldConfig } from '../../../config/optimizePlanConfig';

export interface ScopeSelection {
    attributeType: string;
    values: string[];
}

export function buildScopeFilters(scopeSelections: ScopeSelection[]): PromotionCountFilters {
    const inclusion: PromotionCountFilters['inclusion'] = [];
    for (const { attributeType, values } of scopeSelections) {
        for (const id of values) {
            inclusion.push({ attributeType, attributeId: id });
        }
    }
    return { inclusion, exclusion: [] };
}

const ORIGINAL_VALUE_KEY = 'original_value';
const DEFAULT_VALUE_KEY = 'default_value';

// Returns { value: { min, max }, original_value, default_value }.
function getRangeConstraintValue(row: ConstraintRowData) {
    const originalValue = (row.fields[ORIGINAL_VALUE_KEY] as number) ?? null;
    const defaultValue = (row.fields[DEFAULT_VALUE_KEY] as number) ?? null;
    if ((row.constraint_type ?? 'range') === 'number') {
        return {
            value: { min: row.single_value ?? null, max: row.single_value ?? null },
            original_value: originalValue,
            default_value: defaultValue,
        };
    }
    return {
        value: { min: row.min_value, max: row.max_value },
        original_value: originalValue,
        default_value: defaultValue,
    };
}

// For non-top levels the row has levelDetails; for the top level (depthLevel 0) values are inlined on the row itself.
function buildRangeConstraintEntry(level: Level, kpiData: ConstraintRowData) {
    if (!kpiData.levelDetails || kpiData.levelDetails.length === 0) {
        // Category / top-level: single inline entry.
        return {
            constraint_name: kpiData.name,
            level_details: [
                {
                    attribute_name: kpiData.name,
                    attribute_label: level,
                    ...getRangeConstraintValue(kpiData),
                },
            ],
        };
    }
    return {
        constraint_name: kpiData.name,
        level_details: kpiData.levelDetails.map((detail) => ({
            attribute_name: detail.name,
            attribute_label: level,
            ...getRangeConstraintValue(detail),
        })),
    };
}

interface BuildOptimizationPayloadParams {
    planId: string;
    formData: FormData;
    tableData: ConstraintRowData[];
    level: Level;
    scopeSelections: ScopeSelection[];
    baseInputFields: FormFieldConfig[];
    limitConstraintFields: LimitConstraintFieldConfig[];
}

// Returns top-level numeric field values (e.g. keep_top), excluding those mapped to limit_constraints.
function buildTopLevelNumericFields(
    formData: FormData,
    baseInputFields: FormFieldConfig[],
    limitConstraintFieldNames: Set<string>,
): Record<string, number> {
    const result: Record<string, number> = {};
    for (const field of baseInputFields) {
        if (
            (field.type === 'numberInput' || field.type === 'numberWithContext') &&
            field.name &&
            !limitConstraintFieldNames.has(field.name)
        ) {
            const raw = formData[field.name];
            result[field.name] = (raw != null ? Number(raw) : field.defaultValue) ?? 0;
        }
    }
    return result;
}

export function buildOptimizationPayload({
    planId,
    formData,
    tableData,
    level,
    scopeSelections,
    baseInputFields,
    limitConstraintFields,
}: BuildOptimizationPayloadParams) {
    const attributesSelections = scopeSelections.flatMap(({ attributeType, values }) =>
        values.map((id) => ({ attributeType, attributeId: id })),
    );

    const limitConstraintFieldNames = new Set(limitConstraintFields.map((f) => f.formFieldName));

    const topLevelNumericFields = buildTopLevelNumericFields(formData, baseInputFields, limitConstraintFieldNames);

    const limit_constraints = limitConstraintFields.map((lcf) => ({
        constraint_name: lcf.constraintName,
        operator: lcf.operator,
        value: Number(formData[lcf.formFieldName] ?? 0),
    }));

    return {
        plan_id: planId,
        ...(attributesSelections.length > 0 && { attributes_selections: attributesSelections }),
        objective: formData.objective,
        optimization_start: formData.optimization_start_date,
        ...topLevelNumericFields,
        optimization_attribute_level: level,
        range_constraints: tableData.map((row) => buildRangeConstraintEntry(level, row)),
        limit_constraints,
    };
}
